import {reaction} from 'mobx';
import {mindboxSetCart} from '../http/basketApi';
import {isServerSnapshotTimeValid} from '../utils/serverSnapshotTime';

export const combinedMindboxCartEnabled = () => process.env.REACT_APP_CONFIRMED_PREORDER_CART_ENABLED === 'true' &&
  process.env.REACT_APP_CONFIRMED_PREORDER_QUOTE_ENABLED === 'true';
const id = value => Number.isSafeInteger(value) && value > 0 && value <= 2147483647;
const owner = main => {
  const basket = main.basket, session = basket._session, pre = basket.preorderCart;
  if (!session || basket.snapshotLocked || basket.snapshotMode !== 'enforced' || !basket.ordinaryOwnerReady ||
    !pre?.session || pre.session.authScope !== session.authScope || pre.session.principalId !== session.principalId ||
    (session.authScope === 'authenticated' ? !main.user.isAuth || main.user.user?.id !== session.principalId : main.user.isAuth || main.user.user?.id)) return null;
  return JSON.stringify([basket.sessionEpoch, session.authScope, session.principalId, pre.epoch]);
};
const row = (item, childId, colorId, size, count, now) => {
  const price = item?.product?.price;
  if (!id(childId) || !id(colorId) || !id(count) || item?.id !== childId || item.colorId !== colorId ||
    typeof size !== 'string' || !size.trim() || size.length > 128 || item?.commerce?.mode !== 'enforced' ||
    !isServerSnapshotTimeValid(item?.commerce?.evaluatedAt, now, 300000) || typeof price !== 'number' || !Number.isFinite(price) || price <= 0 ||
    !Number.isSafeInteger(Math.round(price * 100)) || Math.abs(price * 100 - Math.round(price * 100)) > 0.000001) return null;
  // Existing SetCart uses base price, not checkout discounts. Public cards are
  // ephemeral: restored identity-only drafts deliberately contain no price.
  return {id: childId, size, colorId, count, pricePerItem: price};
};
export function projectCombinedMindboxCart(main, now = Date.now()) {
  if (!owner(main)) return null;
  const {basket} = main, pre = basket.preorderCart;
  if (pre.pending || pre.error) return null;
  const rows = [];
  for (const line of basket.basketItems) {
    if (line.status === 'unavailable') continue;
    if (line.status !== 'visible') return null;
    const value = row(line.item, line.child_product_id, line.colorId ?? line.item?.colorId, line.currentSize, line.count, now);
    if (!value) return null; rows.push(value);
  }
  for (const line of pre.lines) {
    if (line.verification === 'unavailable') continue;
    const value = row(line.item, line.identity.childProductId, line.identity.colorId, line.currentSize, line.quantity, now);
    if (!value) return null; rows.push(value);
  }
  const combined = new Map();
  for (const value of rows) {
    const key = JSON.stringify([value.id, value.colorId, value.size]), previous = combined.get(key);
    if (previous) {
      if (previous.pricePerItem !== value.pricePerItem || !id(previous.count + value.count)) return null;
      previous.count += value.count;
    } else combined.set(key, {...value});
  }
  // No invented field distinguishing parts: current SetCart has one SKU list.
  return [...combined.values()].sort((a, b) => a.id - b.id || a.colorId - b.colorId || a.size.localeCompare(b.size));
}
const browserTracking = () => ({
  deviceUUID: document.cookie.replace(/(?:(?:^|.*;\s*)mindboxDeviceUUID\s*=\s*([^;]*).*$)|^.*$/, '$1'),
  userAgent: window.navigator.userAgent,
});
export function createCombinedMindboxCartSync(main, {enabled = combinedMindboxCartEnabled(), send = mindboxSetCart, readTracking = browserTracking} = {}) {
  if (!enabled) return () => {};
  let disposed = false, running = false, queued = null, sent = null, hydration = null, attempt = null, retryTimer = null;
  const cancelRetry = () => {clearTimeout(retryTimer); retryTimer = null;};
  const snapshot = () => {
    const scope = owner(main), lines = projectCombinedMindboxCart(main), tracking = readTracking();
    if (!scope || lines === null || !tracking?.deviceUUID || !tracking.userAgent) return null;
    const value = {scope, lines, deviceUUID: tracking.deviceUUID, userAgent: tracking.userAgent};
    return {...value, key: JSON.stringify(value)};
  };
  const drain = async () => {
    if (running || disposed) return;
    running = true;
    try {
      while (!disposed && queued) {
        const next = queued; queued = null;
        if (snapshot()?.key !== next.key || sent === next.key || attempt?.key !== next.key || attempt.count >= 2 || retryTimer !== null) continue;
        const currentAttempt = attempt;
        currentAttempt.count++;
        let acknowledgement;
        try {
          sent = null; // An unacknowledged send may still have changed the remote cart.
          acknowledgement = await send(next.deviceUUID, next.userAgent, next.lines);
        } catch {} // Unknown completion may already be accepted upstream.
        // Late ACKs grant no writes to either cart or another owner's session.
        if (disposed || snapshot()?.key !== next.key || currentAttempt !== attempt) continue;
        const verified = acknowledgement?.schemaVersion === 'mindbox-cart-ack-v1';
        if (verified && acknowledgement.outcome === 'accepted' && acknowledgement.retryable === false) {
          sent = next.key; // Queue acceptance only; not proof of remote execution.
        } else if (verified && acknowledgement.outcome === 'rejected' && acknowledgement.retryable === false) {
          currentAttempt.count = 2; // Permanent rejection: no automatic/event loop.
        } else if (currentAttempt.count < 2) {
          // At most one delayed retry of this same whole snapshot. Repeated
          // pixel events do not replenish the budget or bypass this delay.
          retryTimer = setTimeout(() => {
            retryTimer = null;
            if (disposed || currentAttempt !== attempt || snapshot()?.key !== next.key) return;
            queued = next; void drain();
          }, 1000);
        }
      }
    } finally {running = false;}
  };
  const changed = () => {
    if (disposed) return;
    const scope = owner(main), pre = main.basket.preorderCart;
    if (scope && !pre.pending && !pre.error && pre.lines.some(line => line.verification !== 'unavailable' &&
      (!line.item || !isServerSnapshotTimeValid(line.item.commerce?.evaluatedAt, Date.now(), 300000)))) {
      const key = JSON.stringify([scope, pre.lines.map(line => [line.lineKey, line.revision, line.item?.commerce?.evaluatedAt])]);
      if (key !== hydration) {hydration = key; Promise.resolve(pre.refresh()).catch(() => {});}
    }
    queued = snapshot();
    if (queued?.key !== attempt?.key) {cancelRetry(); attempt = queued ? {key: queued.key, count: 0} : null;}
    if (queued) void drain();
  };
  const dispose = reaction(() => JSON.stringify([owner(main), projectCombinedMindboxCart(main), main.basket.localSnapshotVersion,
    main.basket.preorderCart?.pending,
    main.basket.preorderCart?.error, main.basket.preorderCart?.lines.map(line => [line.lineKey, line.revision, Boolean(line.item)])]),
  changed, {fireImmediately: true});
  document.addEventListener('generalPixelInited', changed);
  return () => {disposed = true; queued = null; cancelRetry(); dispose(); document.removeEventListener('generalPixelInited', changed);};
}
